import {
  isString,
  isBoolean,
  isArray,
  typeGuardGenerator,
  isArrayOfGenerator,
  unionGuard,
  isRecord,
  isUndefinedOrNull,
  isNumber,
} from '@metools/tcheck';
import dayjs from 'dayjs';
import gql from 'graphql-tag';
import {
  appSyncApolloClient,
  getIdToken,
  useRootStore,
} from '@qsrsoft/myq-core';
import { storeToRefs } from 'pinia';
import utc from 'dayjs/plugin/utc';
import dayjstimezone from 'dayjs/plugin/timezone';
import { isMultiClientUser } from '@/views/multiClient/mtEnterprises';

import { orgCalendar } from '@/views/dashboard/widgets/calendar/constants/constants';

import {
  isValidDateTimeString,
  isStringOrUndefinedOrNull,
  isStringArray,
} from '@qsrsoft/myq-core';

import {
  AnniversaryEvent,
  BirthdayEvent,
  MyQEvent,
  RemoteSubscriptionEvent,
  isBirthdayAnniversaryAPIResponse,
} from '@/views/dashboard/widgets/calendar/models/events';

import {
  MyQCalendar,
  RemoteSubscriptionCalendar,
} from '@/views/dashboard/widgets/calendar/models/myq_calendar';

dayjs.extend(utc);
dayjs.extend(dayjstimezone);

/** @typedef {import('@/views/dashboard/widgets/calendar/models/events').MyQEventJSON} MyQEventJSON */
/** @typedef {import('@/views/dashboard/widgets/calendar/models/events').BirthdayAnniversaryAPIResponse} BirthdayAnniversaryAPIResponse */
/** @typedef {import('@/views/dashboard/widgets/calendar/ui/dialogs/dialog_types').EditMyQEventData} EditMyQEventData */

/**
 * @typedef {Object} CreateEventArgs
 * @property {string} orgId
 * @property {string} title
 * @property {string} start
 * @property {string} end
 * @property {string} createdByUserId
 * @property {string} [calendarId]
 * @property {string} [description]
 * @property {string} [locationId]
 * @property {string} [locationName]
 * @property {string} [letterCommentClass]
 * @property {boolean} [allDay]
 * @property {string} [recurring]
 * @property {string} [recurringEnd]
 * @property {string} [certificationId]
 * @property {string} [originalEventId]
 * @property {string[]} [roleAccessList]
 * @property {string[]} [locationAccessList]
 * @property {boolean} [isFeedPost]
 * @property {boolean} [isPinned]
 */

/**
 * @returns {string} The base URL for the calendar API
 */
export function getCalendarApi() {
  //return 'http://localhost:3264';
  return import.meta.env.VITE_CALENDAR_CLOUD_API_URL ?? '';
}

/**
 * iCal request headers. While a support user is assuming someone, names that
 * user in x-on-behalf-of so iCal answers as them.
 * @returns {Promise<Record<string, string>>}
 */
async function getCalendarAuthHeaders() {
  /** @type {Record<string, string>} */
  const headers = { authorization: await getIdToken() };
  const assumedUserId = localStorage.getItem('assumedUserID');

  if (isString(assumedUserId) && assumedUserId !== '') {
    headers['x-on-behalf-of'] = assumedUserId;
  }

  return headers;
}

/**
 * Creates a new event in the calendar API.
 * @param {CreateEventArgs} args
 * @returns {Promise<MyQEventJSON>}
 */
export async function createCalendarEvent(args) {
  const baseUrl = `${getCalendarApi()}/createEvent`;

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });

  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to create event');
  }

  if (MyQEvent.isMyQEventJSON(json.event)) {
    return json.event;
  } else {
    console.warn('Not a valid event', json.event);
    throw new Error('Not a valid event');
  }
}

/**
 * Creates an event with file attachments via multipart/form-data.
 * @param {CreateEventArgs} args
 * @param {File[]} files
 * @returns {Promise<MyQEventJSON>}
 */
export async function createCalendarEventWithAttachments(args, files) {
  const baseUrl = `${getCalendarApi()}/createEventWithAttachments`;

  const formData = new FormData();

  formData.append('eventMetadata', JSON.stringify(args));

  if (Array.isArray(files)) {
    for (const file of files) {
      formData.append('fileData', file);
    }
  }

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      ...(await getCalendarAuthHeaders()),
    },
    body: formData,
  });

  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to create event with attachments');
  }

  if (MyQEvent.isMyQEventJSON(json.event)) {
    return json.event;
  } else {
    console.warn('Not a valid event', json.event);
    throw new Error('Not a valid event');
  }
}

/**
 * @typedef {Object} EventAttachmentUrlInfo
 * @property {string} id - The attachment ID
 * @property {string} name - The attachment file name
 * @property {string} mimeType - The MIME type of the attachment
 * @property {string} downloadUrl - Pre-signed URL for downloading the attachment
 * @property {string} viewUrl - Pre-signed URL for viewing the attachment
 */

/**
 * @typedef {Object} GetEventAttachmentUrlsResponse
 * @property {EventAttachmentUrlInfo[]} attachments - Array of attachment URL info
 * @property {number} expiresIn - Seconds until the URLs expire (typically 3600 = 1 hour)
 */

/** @type {import('@metools/tcheck').TypeGuard<EventAttachmentUrlInfo>} */
const isEventAttachmentUrlInfo = typeGuardGenerator({
  id: isString,
  name: isString,
  mimeType: isString,
  downloadUrl: isString,
  viewUrl: isString,
});

/** @type {import('@metools/tcheck').TypeGuard<EventAttachmentUrlInfo[]>} */
const isEventAttachmentUrlInfoArray = isArrayOfGenerator(
  isEventAttachmentUrlInfo,
);

/**
 * @description Gets pre-signed URLs for downloading/viewing event attachments.
 * @param {string} orgId - The organization ID.
 * @param {string} eventId - The event ID.
 * @returns {Promise<GetEventAttachmentUrlsResponse>} Object containing attachment URLs and expiry time.
 */
export async function getEventAttachmentUrls(orgId, eventId) {
  const params = new URLSearchParams({ orgId, eventId });
  const response = await fetch(
    `${getCalendarApi()}/getEventAttachmentUrls?${params}`,
    {
      headers: await getCalendarAuthHeaders(),
    },
  );

  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to get event attachment URLs');
  }

  if (
    isRecord(json) &&
    isEventAttachmentUrlInfoArray(json.attachments) &&
    isNumber(json.expiresIn)
  ) {
    return {
      attachments: json.attachments,
      expiresIn: json.expiresIn,
    };
  } else {
    console.warn('Not a valid attachment URLs response', json);
    throw new Error('Not a valid attachment URLs response');
  }
}

/**
 * @description Edits an existing event in the calendar API.
 * @param {EditMyQEventData} args - The arguments for the editEvent function.
 * @returns {Promise<void>}
 */
export async function editCalendarEvent(args) {
  const baseUrl = `${getCalendarApi()}/editEvent`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });

  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to edit event');
  }
}

/**
 * @typedef {Object} EditEventWithAttachmentsArgs
 * @property {string} eventId
 * @property {string} orgId
 * @property {string} title
 * @property {string} start
 * @property {string} end
 * @property {string} lastEditedByUserId
 * @property {string} [calendarId]
 * @property {string} [description]
 * @property {boolean} [allDay]
 * @property {string} [recurring]
 * @property {string} [recurringEnd]
 * @property {string[]} [roleAccessList]
 * @property {string[]} [locationAccessList]
 * @property {string[]} [removeAttachmentIds]
 * @property {boolean} [isFeedPost]
 * @property {number} [feedPostLeadDays]
 * @property {boolean} [isPinned]
 * @property {string} [eventSource]
 */

/**
 * Edits an event with file attachments via multipart/form-data.
 * Supports adding new attachments and removing existing ones.
 * @param {EditEventWithAttachmentsArgs} args - Event metadata including removeAttachmentIds
 * @param {File[]} files - New files to upload
 * @returns {Promise<void>}
 */
export async function editCalendarEventWithAttachments(args, files) {
  const baseUrl = `${getCalendarApi()}/editEventWithAttachments`;

  const formData = new FormData();

  // IMPORTANT: eventMetadata MUST be appended BEFORE file fields
  formData.append('eventMetadata', JSON.stringify(args));

  if (Array.isArray(files)) {
    for (const file of files) {
      formData.append('fileData', file);
    }
  }

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      // Note: Do NOT set Content-Type - browser sets it with boundary
      ...(await getCalendarAuthHeaders()),
    },
    body: formData,
  });

  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to edit event with attachments');
  }
}

/**
 * @description Deletes an event in the calendar API.
 * @param {Object} args - The arguments for deleting an event.
 * @param {string} args.orgId - The organization ID.
 * @param {string} args.eventId - The ID of the event to delete.
 * @returns {Promise<string>} The deleted event ID returned by the API.
 */
export async function deleteCalendarEvent(args) {
  const baseUrl = `${getCalendarApi()}/deleteEvent`;

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to delete event');
  }
  if (isString(json.eventId)) {
    return json.eventId;
  } else {
    console.warn('Not a valid eventId', json.eventId);
    throw new Error('Not a valid eventId');
  }
}

/**
 * @typedef {Object} CreateCalendar
 * @property {string} PK - The partition key of the calendar.
 * @property {string} SK - The sort key of the calendar.
 * @property {string} id - The unique identifier of the calendar.
 * @property {string} orgId - The organization ID.
 * @property {string} title - The calendar title.
 * @property {string} color - The calendar color.
 * @property {boolean} active - Indicates if the calendar is active.
 * @property {string} createdTime - The timestamp when the calendar was created.
 * @property {string} createdByUserId - The ID of the calendar creator.
 * @property {string} lastEditedTime - The timestamp of the last edit.
 * @property {string} lastEditedByUserId - The ID of the last editor.
 * @property {string[]} [roleAccessList] - The roles that are shared with the calendar.
 * @property {string[]} [locationAccessList] - The locations that are shared with the calendar.
 */

const isStringArrayOrUndefinedOrNull = unionGuard(
  isStringArray,
  isUndefinedOrNull,
);

/** @type {import('@metools/tcheck').TypeGuard<CreateCalendar>} */
export const isCreateCalendar = typeGuardGenerator({
  id: isString,
  orgId: isString,
  title: isString,
  color: isString,
  active: isBoolean,
  createdTime: isValidDateTimeString,
  createdByUserId: isString,
  lastEditedTime: isValidDateTimeString,
  lastEditedByUserId: isString,
  roleAccessList: isStringArrayOrUndefinedOrNull,
  locationAccessList: isStringArrayOrUndefinedOrNull,
});

/**
 * @description Creates a new calendar in the calendar API.
 * @param {Object} args - The arguments for the createCalendar function.
 * @param {string} args.orgId - The organization ID.
 * @param {string} args.title - The title of the calendar.
 * @param {string} args.color - The color of the calendar.
 * @param {string} args.createdByUserId - The ID of the user creating the calendar.
 * @param {string[]} args.roleAccessList - The roles that are shared with the calendar.
 * @param {string[]} args.locationAccessList - The locations that are shared with the calendar.
 * @returns {Promise<MyQCalendar>} The full created calendar object returned by the API.
 */
export async function createCalendar(args) {
  const baseUrl = `${getCalendarApi()}/createCalendar`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to Create New Calendar');
  }

  if (isRecord(json) && MyQCalendar.isMyQCalendarJSON(json.calendar)) {
    return new MyQCalendar(json.calendar);
  } else {
    console.warn('Not a valid calendar', json.calendar);
    throw new Error('Not a valid calendar return value');
  }
}

/**
 *  @typedef {Object} EditedCalendarResponse
 *  @property {string} calendarId
 *  @property {string} title
 *  @property {string} color
 *  @property {string[]} roleAccessList
 *  @property {string[]} locationAccessList
 */

/** @type {import('@metools/tcheck').TypeGuard<EditedCalendarResponse>} */
export const isEditedCalendarResponse = typeGuardGenerator({
  calendarId: isString,
  title: isString,
  color: isString,
  roleAccessList:
    isArrayOfGenerator(isString) ||
    isArrayOfGenerator(isStringOrUndefinedOrNull),
  locationAccessList:
    isArrayOfGenerator(isString) ||
    isArrayOfGenerator(isStringOrUndefinedOrNull),
});

/**
 * @description Edits a calendar in the calendar API.
 * @param {Object} args - The arguments for the editCalendar function.
 * @param {Object} args.calendar - The calendar object to edit.
 * @param {string} args.calendar.id - The ID of the calendar being edited.
 * @param {string} args.calendar.orgId - The organization ID.
 * @param {string} args.calendar.title - The new title of the calendar.
 * @param {string} args.calendar.color - The new color of the calendar.
 * @param {string} args.calendar.lastEditedByUserId - The ID of the user making the edit.
 * @param {boolean} args.calendar.active - The new active status of the calendar.
 * @param {string} args.calendar.createdTime - The timestamp when the calendar was created.
 * @param {string} args.calendar.createdByUserId - The ID of the calendar creator.
 * @param {string} args.calendar.lastEditedTime - The timestamp of the last edit.
 * @param {string} args.calendar.lastEditedByUserId - The ID of the last editor.
 * @param {string[]} args.calendar.roleAccessList - The roles that are shared with the calendar.
 * @param {string[]} args.calendar.locationAccessList - The locations that are shared with the calendar.
 * @param {Object} args.calendar.remoteSubscriptions - The remote subscriptions of the calendar.
 * @returns {Promise<EditedCalendarResponse>} The edited calendar object returned by the API.
 */
export async function editCalendar(args) {
  const baseUrl = `${getCalendarApi()}/editCalendar`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });

  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('error: problem editing calendar');
  }
  if (isEditedCalendarResponse(json)) {
    return json;
  } else {
    console.warn('Not a valid calendar', json);
    throw new Error('Not a valid calendar');
  }
}

/**
 * @description Deletes a calendar in the calendar API.
 * @param {Object} args - The arguments for the deleteCalendar function.
 * @param {string} args.orgId - The organization ID.
 * @param {string} args.calendarId - The ID of the calendar to delete.
 * @returns {Promise<string>} The deleted calendar ID returned by the API.
 */
export async function deleteCalendar(args) {
  const baseUrl = `${getCalendarApi()}/deleteCalendar`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('error: problem deleting calendar');
  }
  if (isString(json.calendarId)) {
    return json.calendarId;
  } else {
    console.warn('Not a valid id', json.calendarId);
    throw new Error('Not a valid id');
  }
}

/**
 * @description Deactivates a calendar in the calendar API.
 * @param {Object} args - The arguments for the deactivateCalendar function.
 * @param {string} args.orgId - The organization ID.
 * @param {string} args.id - The ID of the calendar to deactivate.
 * @returns {Promise<string>} The deactivated calendar ID returned by the API.
 */
export async function deactivateCalendar(args) {
  const baseUrl = `${getCalendarApi()}/deactivateCalendar`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });

  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('error: Failed to deactivate calendar');
  }
  if (isString(json.id)) {
    return json.id;
  } else {
    console.warn('Not a valid id', json.id);
    throw new Error('Not a valid id');
  }
}

/**
 * @typedef {object} GetCalendarEventsResponse
 * @property {MyQEvent[]} events
 * @property {RemoteSubscriptionEvent[]} subscriptionEvents
 */

/**
 * @description Gets events from the calendar API.
 * @param {Object} args - The arguments for the getEvents function.
 * @param {string} args.orgId - The organization ID.
 * @param {Object} args.range - The date range for fetching events.
 * @param {string} args.range.start - The start date/time (ISO format).
 * @param {string} args.range.end - The end date/time (ISO format).
 * @returns {Promise<GetCalendarEventsResponse>} A list of event objects returned by the API.
 */
export async function getCalendarEvents(args) {
  const orgId = args.orgId;
  const start = args.range.start;
  const end = args.range.end;
  const baseUrl = `${getCalendarApi()}/getEvents?orgId=${orgId}&startDate=${start}&endDate=${end}`;

  const response = await fetch(baseUrl, {
    headers: await getCalendarAuthHeaders(),
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Failed to fetch events');
  }

  /** @type {MyQEvent[]} */
  const events = [];
  /** @type {RemoteSubscriptionEvent[]} */
  const subscriptionEvents = [];

  if (isArray(json.subscriptionEvents)) {
    for (const subscription of json.subscriptionEvents) {
      if (RemoteSubscriptionEvent.isRemoteSubscriptionEventJSON(subscription)) {
        subscriptionEvents.push(new RemoteSubscriptionEvent(subscription));
      } else {
        console.warn('Not a valid subscription', subscription);
      }
    }
  } else {
    console.warn('subscriptionEvents is not an array', json.subscriptionEvents);
  }

  if (isArray(json.events)) {
    for (const event of json.events) {
      if (MyQEvent.isMyQEventJSON(event)) {
        const isClassEvent = event.calendarId === 'classes';
        const shouldClearCalendarId =
          isClassEvent ||
          event.calendarId === orgCalendar ||
          event.calendarId === null ||
          event.calendarId === undefined ||
          event.calendarId === '';

        if (shouldClearCalendarId) {
          events.push(
            new MyQEvent({
              ...event,
              calendarId: '',
              ...(isClassEvent && { isClassEvent: true }),
            }),
          );
        } else {
          events.push(new MyQEvent(event));
        }
      } else {
        console.warn('Not a valid event', event);
      }
    }
  } else {
    console.warn('events is not an array', json.events);
  }
  return { events, subscriptionEvents };
}

/**
 * @description Gets calendars from the calendar API
 * @param {Object} args - The arguments for the getCalendars function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.userId - The user ID
 * @returns {Promise<MyQCalendar[]>} The calendars
 */
export async function getCalendars(args) {
  const baseUrl = `${getCalendarApi()}/getCalendars?orgId=${args.orgId}&userId=${args.userId}`;

  const response = await fetch(baseUrl, {
    method: 'GET',
    headers: {
      ...(await getCalendarAuthHeaders()),
    },
  });

  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('error: could not get calendars');
  }

  const calendars = [];
  if (isArray(json.calendars)) {
    for (const calendar of json.calendars) {
      if (MyQCalendar.isMyQCalendarJSON(calendar)) {
        calendars.push(new MyQCalendar(calendar));
      } else {
        console.warn('Not a valid calendar', calendar);
      }
    }
  } else {
    console.warn('calendars is not an array', json.calendars);
  }
  return calendars;
}

/**
 * @description Loads all events (regular and subscription) from the calendar API
 * @param {object} params - The parameters for loading calendar events
 * @param {string} params.start - The start date
 * @param {string} params.end - The end date
 * @param {string} params.orgId - The organization ID
 * @returns {Promise<GetCalendarEventsResponse>} The events
 */
export async function loadCalendarEvents({ start, end, orgId }) {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  const { events, subscriptionEvents } = await getCalendarEvents({
    orgId,
    range: {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
    },
  });

  events.sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );
  subscriptionEvents.sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
  );
  return { events, subscriptionEvents };
}

/** @type {import('@metools/tcheck').TypeGuard<CalendarSubscriptionResponse>} */
export const isCalendarSubscriptionResponse = typeGuardGenerator({
  id: isString,
  myqCalendarId: isString,
});

/**
 * @typedef {Object} CalendarSubscriptionResponse
 * @property {string} id
 * @property {string} myqCalendarId
 */

/**
 * @description Adds a calendar to the user's subscriptions
 * @param {Object} args - The arguments for the addCalendarToSubscriptions function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.url - The URL of the calendar to add to the user's subscriptions
 * @param {string} args.calendarName - The name of the calendar to add to the user's subscriptions
 * @param {string} args.color - The color of the calendar to add to the user's subscriptions
 * @returns {Promise<CalendarSubscriptionResponse>} The calendar ID returned by the API.
 */
export async function addCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/addCalendarSubscription`;

  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });

  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    const error = new Error(json);

    throw error;
  }
  if (isCalendarSubscriptionResponse(json)) {
    return json;
  } else {
    console.warn('Not a valid calendar subscription', json);
    throw new Error('Not a valid calendar subscription');
  }
}

/**
 * @description Gets the user's calendar subscriptions
 * @param {string} orgId - The organization ID
 * @returns {Promise<RemoteSubscriptionCalendar[]>} The calendar subscriptions
 */
export async function getCalendarSubscriptions(orgId) {
  const baseUrl = `${getCalendarApi()}/getCalendarSubscriptions?orgId=${orgId}`;
  const response = await fetch(baseUrl, {
    method: 'GET',
    headers: {
      ...(await getCalendarAuthHeaders()),
    },
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Error getting calendar subscriptions');
  }

  const subscriptions = [];

  if (isArray(json.subscriptions)) {
    for (const subscription of json.subscriptions) {
      if (
        RemoteSubscriptionCalendar.isRemoteSubscriptionEventJSON(subscription)
      ) {
        subscriptions.push(new RemoteSubscriptionCalendar(subscription));
      } else {
        console.warn('Not a valid subscription', subscription);
      }
    }
  } else {
    console.warn('subscriptions is not an array', json.subscriptions);
  }
  return subscriptions;
}

/**
 * @typedef {Object} EditCalendarSubscriptionResponse
 * @property {string} subscriptionId - The subscription ID
 * @property {string} calendarName - The updated calendar name
 * @property {string} color - The updated color
 */

/** @type {import('@metools/tcheck').TypeGuard<EditCalendarSubscriptionResponse>} */
export const isEditCalendarSubscriptionResponse = typeGuardGenerator({
  subscriptionId: isString,
  calendarName: isString,
});

/**
 * @description Gets a calendar subscription by ID
 * @param {Object} args - The arguments for the editCalendarSubscription function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.subscriptionId - The ID of the calendar subscription to edit
 * @param {string} args.calendarName - The name of the calendar to edit
 * @param {string} args.calendarId - The ID of the calendar to edit the subscription from
 * @param {string} args.color - The color of the calendar to edit the subscription
 * @param {string[]} args.roleAccessList - The roles that are shared with the subscription
 * @param {string[]} args.locationAccessList - The locations that are shared with the subscription
 * @returns {Promise<EditCalendarSubscriptionResponse>} The edited calendar subscription
 */
export async function editCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/editCalendarSubscription`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('Error editing calendar subscription');
  }
  if (isEditCalendarSubscriptionResponse(json)) {
    return json;
  } else {
    console.warn('Not a valid calendar subscription', json);
    throw new Error('Not a valid calendar subscription');
  }
}

/**
 * @description Deletes a calendar subscription
 * @param {Object} args - The arguments for the deleteCalendarSubscription function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.subscriptionId - The ID of the calendar subscription to delete
 * @param {string} args.calendarId - The ID of the calendar to delete the subscription from
 * @returns {Promise<string>} The deleted calendar subscription ID returned by the API.
 */
export async function deleteCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/deleteCalendarSubscription`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('Error deleting calendar subscription');
  }
  if (isString(json.subscriptionId)) {
    return json.subscriptionId;
  } else {
    console.warn('Not a valid calendar subscription', json);
    throw new Error('Not a valid calendar subscription');
  }
}

/**
 * @description syncs the calendar subscriptions
 * @param {Object} args - The arguments for the syncCalendarSubscriptions function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.subscriptionId - The ID of the calendar subscription to sync
 * @returns {Promise<string>} The synced calendar subscriptions returned by the API.
 */
export async function syncCalendarSubscriptions(args) {
  const baseUrl = `${getCalendarApi()}/forceSyncCalendar`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error('Error syncing calendar subscriptions');
  }
  if (isString(json.subscriptionId)) {
    return json.subscriptionId;
  } else {
    console.warn('Not a valid calendar subscription', json);
    throw new Error('Not a valid calendar subscription');
  }
}

/**
 * @type {import('@metools/tcheck').TypeGuard<MyqCalendarSubscription>}
 */
export const isMyqCalendarSubscription = typeGuardGenerator({
  calendarIds: isArrayOfGenerator(isString),
  id: isString,
  orgId: isString,
  calendarName: isString,
});

/** @type {import('@metools/tcheck').TypeGuard<MyqCalendarSubscription[]>} */
export const isMyqCalendarSubscriptionArray = isArrayOfGenerator(
  isMyqCalendarSubscription,
);

/**
 * @typedef {Object} MyqCalendarSubscription
 * @property {string[]} calendarIds
 * @property {string} id
 * @property {string} orgId
 * @property {string} calendarName
 */

/**
 * @typedef {Object} MyqCalendarSubscriptions
 * @property {MyqCalendarSubscription[]} subscriptions
 */

/**
 * @description Gets the user's MyQ calendar subscriptions
 * @param {string} orgId - The organization ID
 * @returns {Promise<MyqCalendarSubscription[]>} The MyQ calendar subscriptions
 */
export async function getMyqCalendarSubscriptions(orgId) {
  const baseUrl = `${getCalendarApi()}/getMyqCalendarSubscriptions?orgId=${orgId}`;
  const response = await fetch(baseUrl, {
    method: 'GET',
    headers: await getCalendarAuthHeaders(),
  });
  const json = await response.json();

  if (!response.ok) {
    console.error(json);
    throw new Error(json?.error ?? 'Error getting myq calendar subscriptions');
  }

  if (isMyqCalendarSubscriptionArray(json.subscriptions)) {
    return json.subscriptions;
  } else {
    console.warn('Not a valid myq calendar subscription', json);
    throw new Error('Not a valid myq calendar subscription');
  }
}

/**
 * @description Adds a MyQ calendar subscription
 * @param {Object} args - The arguments for the addMyqCalendarSubscription function
 * @param {string[]} args.calendarIds - The calendar IDs to add to the user's subscriptions
 * @param {string} args.orgId - The organization ID
 * @param {string} args.calendarName - The name of the calendar to add to the user's subscriptions
 * @returns {Promise<MyqCalendarSubscription>} The added calendar subscription returned by the API.
 */
export async function addMyqCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/addMyqCalendarSubscription`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.error ?? 'Error adding myq calendar subscription');
  }
  if (isMyqCalendarSubscription(json.calendar)) {
    return json.calendar;
  } else {
    console.warn('Not a valid myq calendar subscription', json);
    throw new Error('Not a valid myq calendar subscription');
  }
}

/**
 * @description Deletes a MyQ calendar subscription
 * @param {Object} args - The arguments for the deleteMyqCalendarSubscription function
 * @param {string} args.orgId - The organization ID
 * @param {string} args.id - The ID of the calendar subscription to delete
 * @returns {Promise<string>} The deleted calendar subscription ID returned by the API.
 */
export async function deleteMyqCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/deleteMyqCalendarSubscription`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('Error deleting myq calendar subscription');
  }
  if (isString(json.id)) {
    return json.id;
  } else {
    console.warn('Not a valid calendar subscription', json);
    throw new Error('Not a valid calendar subscription');
  }
}

/**
 * @description Updates a MyQ calendar subscription
 * @param {Object} args - The arguments for the updateMyqCalendarSubscription function
 * @param {string} args.calendarSubId - The ID of the calendar subscription to update
 * @param {string} args.orgId - The organization ID
 * @param {string} args.calendarName - The name of the calendar to update
 * @param {string[]} args.calendarIds - The calendar IDs to update the subscription with
 * @returns {Promise<MyqCalendarSubscription>} The updated calendar subscription returned by the API.
 */
export async function updateMyqCalendarSubscription(args) {
  const baseUrl = `${getCalendarApi()}/updateMyqCalendarSubscription`;
  const response = await fetch(baseUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await getCalendarAuthHeaders()),
    },
    body: JSON.stringify(args),
  });
  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error(json.error ?? 'Error updating myq calendar subscription');
  }
  if (isMyqCalendarSubscription(json.calendarSub)) {
    return json.calendarSub;
  } else {
    console.warn('Not a valid myq calendar subscription', json);
    throw new Error('Not a valid myq calendar subscription');
  }
}

/**
 * @typedef {Object} UsersVisibilityResponse
 * @property {string[]} hiddenBirthdayUsers - GEIDs of users who have hidden their birthday
 * @property {string[]} hiddenAnniversaryUsers - GEIDs of users who have hidden their anniversary
 */

/**
 * Gets visibility settings for users by GEIDS
 * @param {object} args
 * @param {string[]} args.geids - Array of GEIDS
 * @param {'birthday' | 'anniversary' | 'both'} args.type - Type of visibility to check
 * @param {string} args.orgId - Organization ID
 * @returns {Promise<UsersVisibilityResponse>} Users visibility response
 */
export async function getUsersVisibilityByGEIDS(args) {
  const accountsBaseURL =
    import.meta.env.VITE_APP_ACCOUNTS_APP_RUNNER_BASE_URL ?? '';
  const url = `${accountsBaseURL}/accounts/getUsersVisibilityByGEIDS`;

  const token = await getIdToken();
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': token,
    },
    body: JSON.stringify({
      geids: args.geids,
      type: args.type,
      orgId: args.orgId,
    }),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  const json = await response.json();

  if (
    !isRecord(json) ||
    !isArray(json.hiddenBirthdayUsers) ||
    !isArray(json.hiddenAnniversaryUsers)
  ) {
    throw new Error('Invalid response format');
  }
  const hiddenBirthdays = json.hiddenBirthdayUsers.map((id) => String(id));
  const hiddenAnniversaries = json.hiddenAnniversaryUsers.map((id) =>
    String(id),
  );

  return {
    hiddenBirthdayUsers: hiddenBirthdays,
    hiddenAnniversaryUsers: hiddenAnniversaries,
  };
}

//
/**
 * @description Fetches the roster events
 * @param {Object} args - The arguments for the fetchRosterEvents function
 * @param {string[]} args.locations - The locations to fetch the roster events for
 * @param {string} args.orgId - The organization ID
 * @param {string} args.startDate - The start date
 * @param {string} args.endDate - The end date
 * @returns {Promise<BirthdayAnniversaryAPIResponse[]>} The roster events
 */
export async function fetchRosterEvents(args) {
  // No stores means no roster to fetch. Worth short-circuiting to avoid unnecessary API calls and errors.

  if (args.locations.length === 0) {
    return [];
  }

  const locs = args.locations.join(',');

  // Multi-tenant orgs read roster data from the MT endpoint; everyone else
  // keeps the existing v2 endpoint. `isMultiClientUser` owns the rule (see
  // mtEnterprises.js) so this stays in step with route resolution and the
  // sidebar menuFilters — notably McDonald's ZA, which is on the MT stack
  // while McDonald's elsewhere is not.
  const { enterpriseClient, countryCode } = storeToRefs(useRootStore());
  const isMt = isMultiClientUser(enterpriseClient.value, countryCode.value);

  const baseUrl = isMt
    ? '/data_layer/mt/v1/people/employee-birthdays-anniversaries'
    : '/data_layer/v1/people/employee-birthdays-anniversaries';

  const queryParams = new URLSearchParams({
    employmentStatus: 'active',
    orgId: args.orgId,
    nsn: locs,
    startDate: args.startDate,
    endDate: args.endDate,
    selectCols: 'firstName,lastName,geid,birthday,orgStartDate,storeNum',
  });

  const url = `${baseUrl}?${queryParams.toString()}`;
  const token = await getIdToken();
  const response = await fetch(
    `${import.meta.env.VITE_APP_REPORT_API_BASE_URL}${url}`,
    { headers: { 'x-auth-token': token } },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const json = await response.json();

  if (!isRecord(json) || !isArray(json.result)) {
    throw new Error('Invalid response format');
  }

  // Return raw validated results - no visibility filtering yet
  return json.result.filter(isBirthdayAnniversaryAPIResponse);
}

/**
 * @description Gets the filtered roster events
 * @param {Object} args - The arguments for the getFilteredRosterEvents function
 * @param {BirthdayAnniversaryAPIResponse[]} args.rosterData - The roster data
 * @param {string} args.orgId - The organization ID
 * @param {string} args.startDate - The start date
 * @param {string} args.endDate - The end date
 * @returns {Promise<{birthdays: BirthdayEvent[], anniversaries: AnniversaryEvent[]}>} The filtered roster events
 */
export async function getFilteredRosterEvents(args) {
  const { rosterData, orgId, startDate, endDate } = args;

  // Pre-compute events and filter to date range
  /** @type {Array<{item: BirthdayAnniversaryAPIResponse, birthday: BirthdayEvent | null, anniversary: AnniversaryEvent | null}>} */
  const eventsInRange = [];

  for (const item of rosterData) {
    /** @type {BirthdayEvent | null} */
    let birthdayInRange = null;
    /** @type {AnniversaryEvent | null} */
    let anniversaryInRange = null;

    try {
      const bday = BirthdayEvent.fromJSON(item, orgId);
      const bdayInRange = bday.getBirthdayBetweenDates(startDate, endDate);
      if (bdayInRange) {
        birthdayInRange = bdayInRange;
      }
    } catch {
      // Invalid birthday
    }

    try {
      const anni = AnniversaryEvent.fromJSON(item, orgId);
      const anniInRange = anni.getAnniversaryBetweenDates(startDate, endDate);
      if (anniInRange) {
        anniversaryInRange = anniInRange;
      }
    } catch {
      // Invalid anniversary
    }

    if (birthdayInRange || anniversaryInRange) {
      eventsInRange.push({
        item,
        birthday: birthdayInRange,
        anniversary: anniversaryInRange,
      });
    }
  }

  // Get GEIDs only for those in range
  const geids = eventsInRange
    .map((e) => String(e.item.geid))
    // String(undefined) returns "undefined", String(null) returns "null", so we filter out those
    .filter((geid) => geid && geid !== 'undefined' && geid !== 'null');

  // Fetch visibility for filtered GEIDs
  let hiddenBirthdaySet = new Set();
  let hiddenAnniversarySet = new Set();

  if (geids.length > 0) {
    try {
      const visibility = await getUsersVisibilityByGEIDS({
        geids,
        type: 'both',
        orgId,
      });
      hiddenBirthdaySet = new Set(
        (visibility.hiddenBirthdayUsers ?? []).map(String),
      );
      hiddenAnniversarySet = new Set(
        (visibility.hiddenAnniversaryUsers ?? []).map(String),
      );
    } catch (e) {
      console.error('Error getting users visibility:', e);
      return { birthdays: [], anniversaries: [] };
    }
  }

  // Build final arrays
  const birthdays = [];
  const anniversaries = [];

  for (const { item, birthday, anniversary } of eventsInRange) {
    const stringGeid = String(item.geid);
    if (birthday && !hiddenBirthdaySet.has(stringGeid)) {
      birthdays.push(birthday);
    }
    if (anniversary && !hiddenAnniversarySet.has(stringGeid)) {
      anniversaries.push(anniversary);
    }
  }

  return { birthdays, anniversaries };
}

/**
 * @typedef {object} CommsHubOrg Result of the Comms Hub organization query.
 * @property {string} orgId The organization ID.
 * @property {string} orgName The name of the organization.
 * @property {string | number} [clientId] The client Id (optional).
 * @property {string} [enterpriseClient] The enterprise client, e.g. CommsHub (optional).
 * @property {string[]} [locations] The locations associated with the CommsHub organization (optional).
 */

const isNumberOrString = unionGuard(isNumber, isString);

/** @type {import('@metools/tcheck').TypeGuard<CommsHubOrg>} */
export const isCommsHubOrg = typeGuardGenerator({
  orgId: isString,
  orgName: isString,
  clientId: unionGuard(isNumberOrString, isUndefinedOrNull),
  enterpriseClient: unionGuard(isString, isUndefinedOrNull),
  locations: isStringArrayOrUndefinedOrNull,
});

/**
 * Gets Comms Hub organizations for the specified location IDs, filtered to
 * those whose audience matches the requesting user's org.
 * @param {object} args
 * @param {string} args.orgId The requesting user's current organization ID.
 * @param {string[]} args.locationIds Location IDs for which we're retrieving Comms Hub organizations.
 * @param {string} [args.service] Content being listed for (e.g. `calendar`);
 *   hubs the org turned that service off for are left out. Omit to list every hub.
 * @returns {Promise<CommsHubOrg[]>} A promise that resolves to an array of Comms Hub organizations.
 */
export async function getCommsHubOrgs(args) {
  const baseUrl = 'https://universal.dev-home.myqsrsoft.com/messaging/api'; // DEV ONLY (#6662 UAT): universal back end, do not merge to master
  const url = `${baseUrl}/commshub/v2/getCommsHubOrgs`;
  const body = {
    orgId: args.orgId,
    locationIds: args.locationIds,
    ...(args.service ? { service: args.service } : {}),
  };

  const idToken = await getIdToken();

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-auth-token': idToken,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching Comms Hub organizations: ${response.statusText}`,
    );
  }

  const json = await response.json();

  if (!isRecord(json) || !isArray(json.orgs)) {
    throw new Error(
      'Invalid response format: Expected an array of organizations',
    );
  }
  return json.orgs.filter(isCommsHubOrg);
}

/**
 * Gets the Comms Hub organizations sharing content into the given org.
 *
 * The hub-to-hub analogue of getCommsHubOrgs: a CommsHub has no location
 * membership pointing at other hubs, so its discovery source is the content
 * shares managed in the accounts Administration tab. The response entries
 * match getCommsHubOrgs' shape, so both feed the same calendar-building code.
 * @param {string} userOrgId The viewing CommsHub organization ID.
 * @returns {Promise<CommsHubOrg[]>} The organizations sharing into it.
 */
export async function getSharedCommsHubOrgs(userOrgId) {
  if (!isString(userOrgId) || userOrgId === '') {
    throw new Error('userOrgId is required');
  }

  const queryParams = new URLSearchParams({ userOrgId });
  const url = `${getCalendarApi()}/getIncomingSharedOrgs?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      ...(await getCalendarAuthHeaders()),
    },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching shared Comms Hub organizations: ${response.statusText}`,
    );
  }

  const json = await response.json();

  if (!isRecord(json) || !isArray(json.orgs)) {
    throw new Error(
      'Invalid response format: Expected an array of organizations',
    );
  }
  return json.orgs.filter(isCommsHubOrg);
}

/**
 * @description Gets the comms hub events
 * @param {Object} args - The arguments for the getCommsHubEvents function
 * @param {string[]} args.commsHubOrgIds - Array of organization IDs
 * @param {string} args.userOrgId - The user's organization ID
 * @param {string} args.startDate - ISO datetime string for start date
 * @param {string} args.endDate - ISO datetime string for end date
 * @returns {Promise<MyQEvent[]>} The comms hub events
 */
export async function getCommsHubEvents(args) {
  if (!isString(args.userOrgId) || args.userOrgId === '') {
    throw new Error('userOrgId is required');
  }
  if (!isArray(args.commsHubOrgIds)) {
    throw new Error('commsHubOrgIds is required and must be an array');
  }
  if (!isString(args.startDate) || args.startDate === '') {
    throw new Error('startDate is required');
  }
  if (!isString(args.endDate) || args.endDate === '') {
    throw new Error('endDate is required');
  }

  const queryParams = new URLSearchParams({
    commsHubOrgIds: args.commsHubOrgIds.join(','),
    userOrgId: args.userOrgId,
    startDate: args.startDate,
    endDate: args.endDate,
  });

  const baseUrl = `${getCalendarApi()}/getCommsHubEvents?${queryParams.toString()}`;

  const response = await fetch(baseUrl, {
    method: 'GET',
    headers: {
      ...(await getCalendarAuthHeaders()),
    },
  });

  const json = await response.json();
  if (!response.ok) {
    console.error(json);
    throw new Error('Error getting comms hub events');
  }
  const events = [];
  if (isRecord(json) && isRecord(json.commsHubEvents)) {
    for (const [orgId, orgData] of Object.entries(json.commsHubEvents)) {
      if (isRecord(orgData) && isArray(orgData.events)) {
        for (const event of orgData.events) {
          if (MyQEvent.isMyQEventJSON(event)) {
            events.push(
              new MyQEvent({
                ...event,
                isCommsHubEvent: true,
                calendarId: `commshub-org-${orgId}`,
              }),
            );
          } else {
            console.warn('Not a valid event', event);
          }
        }
      }
      if (isRecord(orgData) && isArray(orgData.subscriptionEvents)) {
        for (const event of orgData.subscriptionEvents) {
          if (RemoteSubscriptionEvent.isRemoteSubscriptionEventJSON(event)) {
            events.push(
              new MyQEvent({
                ...event,
                isCommsHubEvent: true,
                calendarId: `commshub-org-${orgId}`,
              }),
            );
          }
        }
      }
    }
  }
  return events;
}

/**
 * Gets all CommsHub location storeIds for the specified organization
 * @param {string} orgId - The organization ID (with 'org-' prefix)
 * @returns {Promise<string[]>}
 */
export async function getCommsHubOrgLocations(orgId) {
  if (!isString(orgId) || orgId === '') {
    throw new Error('orgId is required');
  }

  const resp = await appSyncApolloClient.query({
    fetchPolicy: 'no-cache',
    variables: { orgId },
    query: gql`
      query commsHubGetAllCommsOrgLocations($orgId: String!) {
        commsHubGetAllCommsOrgLocations(orgId: $orgId) {
          storeId
        }
      }
    `,
  });
  const locations = isArray(resp?.data?.commsHubGetAllCommsOrgLocations)
    ? resp.data.commsHubGetAllCommsOrgLocations
    : [];

  /** @type {string[]} */
  const storeIds = [];
  for (const loc of locations) {
    if (isString(loc.storeId)) {
      storeIds.push(loc.storeId);
    } else {
      console.debug('Invalid CommsHub location:', loc);
    }
  }
  return storeIds;
}
